﻿// #region: Class definition *********************************************************************/
/**
 * Generic validation library.
 * Provides comprehensive client-side form validation functionality including HTML5 validation,
 * custom validation rules, error display, and integration with web components.
 * 
 * @class Validation
 * @description Singleton class that handles validation on data elements and forms.
 * Supports various validation types including text, email, date/time, number, file validation,
 * and custom validation rules. Integrates with the validator component for error display.
 * 
 * @example
 * // Basic form validation
 * const isValid = validation.validateForm({
 *     container: document,
 *     form: document.querySelector('#myForm'),
 *     scrollOffset: 100
 * });
 * 
 * @example
 * // Custom validation with callback
 * const isValid = validation.validateForm({
 *     container: document,
 *     customValidation: () => {
 *         // Custom validation logic
 *         return true; // or false
 *     }
 * });
 * 
 * @example
 * // Individual element validation
 * const isValid = validation.validateElement({
 *     element: document.querySelector('#myInput')
 * });
 * 
 * @dependencies
 * - core: Core utility functions.
 * - dating: Date/time parsing and validation.
 * - validator: Error display component.
 * - eventBroadcaster: Event system for form registration.
 * - resources: Localization resources.
 */
window.validation = new class {

    // #region: Private Fields *******************************************************************/
    /**
     * Timeout for hiding error messages.
     * @type {number}
     * @private
     */
    #hideTimeOut;
    
    /**
     * Regular expression for validating element types that should be validated.
     * @type {RegExp}
     * @private
     */
    #validateTypes;
    // #endregion ********************************************************************************/

    // #region: Constructor **********************************************************************/
    /**
     * Initializes the validation system.
     * Sets up validation types regex, subscribes to form registration events,
     * and initializes validation message resources.
     * 
     * @constructor
     * @memberof Validation
     */
    constructor() {

        // Magic numbers.
        this.#hideTimeOut = 0;

        /**
         * Regular expression matching all HTML input types that should be validated.
         * Includes text, email, number, date, file, select, textarea, and other form elements.
         * @type {RegExp}
         * 
         * @private
         */
        this.#validateTypes = /^(?:checkbox|color|date|datetime|datetime-local|email|file|month|number|password|radio|range|search|select-multiple|select-one|tel|text|textarea|time|url|week)$/ui;

        /**
         * Localized validation messages from the resource system.
         * Can be overridden per form or element using data attributes.
         * @type {Object}
         * 
         * @public
         */
        this.validationMessages = resources.validationMessages;

        // Listen.
        eventBroadcaster.subscribe({ message: broadcasts.ADD_VALIDATORS, subscriber: this });
    }
    // #endregion ********************************************************************************/

    // #region: Build ****************************************************************************/
    /**
     * Returns string representation of the validation instance.
     * @returns {string} The string representation of this class.
     * @memberof Validation
     */
    toString() {
        return "[validation]";
    }

    /**
     * Returns string representation of the validation class.
     * @returns {string} The string representation of this class.
     * 
     * @static
     * @memberof Validation
     */
    static toString() {
        return "[validation]";
    }

    /** 
     * Returns the version number of the validation library.
     * @returns {string} The version number of this class.
     * 
     * @static
     * @memberof Validation
     */
    static version() {
        return "1.0.0";
    }
    // #endregion ********************************************************************************/

    // #region: Events ***************************************************************************/
    /**
     * Handles broadcast events from the event system.
     * Processes form registration events and sets up validation for form elements.
     * 
     * @param {Object} params The broadcast event parameters.
     * @param {Object} params.detail The event detail containing form and validation data.
     * @param {HTMLFormElement} [params.detail.form] The form element to set up validation for.
     * @param {Object} [params.detail.validationMessages] Custom validation messages.
     * @param {string} params.message The broadcast message type.
     * 
     * @memberof Validation
     */
    handleBroadcast({ detail, message }) {
        switch (message) {

            case broadcasts.ADD_VALIDATORS: {
                // Only set custom messages when provided.
                if (detail?.validationMessages) {
                    this.validationMessages = detail.validationMessages;
                }

                const form = detail?.form;

                if (form) {
                    form.addEventListener("submit", this.onFormSubmitHandler.bind(this), { capture: true });
                    form.addEventListener("reset", this.onFormResetHandler.bind(this), { capture: true });

                    // All form elements.
                    [...form.elements].forEach((formelement) => {
                        this.addElementValidation(formelement);
                    });

                    // Any elements we want to force a validator on.
                    [...document.querySelectorAll("[data-validator]")].forEach((element) => {
                        this.addElementValidation(element, true);
                    });

                    // Add validation to all web components with validate methods in the form.
                    const webComponents = [...form.querySelectorAll('*')].filter(el => 
                        el.tagName && el.tagName.includes('-') && typeof el.validate === 'function'
                    );
                    webComponents.forEach((element) => {
                        this.addElementValidation(element, true);
                    });
                }

                break;
            }

            default:
                break;

        }
    }

    /**
     * Handles form reset events.
     * Clears all validation errors and resets form element states.
     * 
     * @param {Event} event The form reset event.
     * @param {HTMLFormElement} event.target The form being reset.
     * 
     * @memberof Validation
     */
    onFormResetHandler(event) {
        this.clear(event.target);

        [...event.target.elements].forEach((element) => {
            element.validator?.hideError();
        });
    }

    /**
     * Handles form submission events and performs validation.
     * Prevents form submission if validation fails, unless formnovalidate attribute is present.
     * 
     * @param {Event} event The form submit event.
     * @param {HTMLFormElement} event.target The form being submitted.
     * 
     * @memberof Validation
     */
    onFormSubmitHandler(event) {
        // It's an actual intended submit, check if we should validate in the first place.
        const bypassSubmit = !!(this.hasAttribute && this.hasAttribute("formnovalidate"));

        // Should we bypass validation or are we not valid (last one in the checks).
        if (!bypassSubmit && !this.validateForm({ container: document, form: event.target })) {
            event.preventDefault();
        }

        // If we are here the submit will normally execute.
    }

    /**
     * Handles HTML5 constraint validation invalid events.
     * Displays error messages using the validator component.
     * 
     * @param {Event} event The invalid event.
     * @param {HTMLElement} event.target The element that failed validation.
     * @param {Object} [event.detail] Custom event detail.
     * @param {string} [event.detail.message] Custom error message.
     * 
     * @memberof Validation
     */
    onInvalidHandler(event) {
        event.preventDefault();
        event.target.validator?.showError({ message: event.detail?.message ?? this.getMessage(event.target) });
    }
    // #endregion ********************************************************************************/

    // #region: Public Methods *******************************************************************/
    /**
     * Attaches validation events to the provided input element or web component.
     * Creates a validator instance and binds it to the element.
     * For web components with validate() methods, creates a custom validator that calls the component's validate() method.
     * Only one validator is allowed per element; existing validators are overwritten.
     * @param {HTMLElement} element - The input element or web component to add validation to.
     * @param {boolean} [force=false] - When true, allows validation on non-standard element types.
     * 
     * @memberof Validation
     */
    addElementValidation(element, force = false) {
        // Check if this is a web component with a validate method.
        const isWebComponent = element.tagName && element.tagName.includes('-') && typeof element.validate === 'function';
        
        // Only attach validators to the elements we're interested in.
        const elementType = this.#getElementType(element);
        if (this.#validateTypes.test(elementType) || isWebComponent || force) {
            const validatorParent = element.dataset?.validatorparent;
            const validateOnBlur = element.hasAttribute("data-validate-onblur");

            element.addEventListener("invalid", this.onInvalidHandler.bind(this), { capture: true });
            element.validator = new validator({
                field: element,
                validatorParent: validatorParent,
                validateOnBlur: validateOnBlur,
                hideTimeout: this.#hideTimeOut
            });
        }
    }

    /**
     * Gets custom validation messages for HTML5 constraint validation.
     * Provides localized error messages instead of browser defaults.
     * @param {HTMLElement} element The element to get validation messages for.
     * @param {boolean} [firstOnly=true] True to return only the first message, false to concatenate all messages.
     * @returns {string} The localized validation message(s).
     * 
     * @memberof Validation
     */
    getMessage(element, firstOnly = true) {

        // Local function to get first 'true' property.
        const getFirst = (validity) => {
            for (const key in validity) {
                if (key !== "valid" && validity[key]) {
                    return key;
                }
            };

            return undefined;
        };

        if (firstOnly) {
            const first = getFirst(element.validity);
            return this.#createMessage(element, first);
        }
        else {
            let errorMessage = "";

            /* eslint-disable-next-line guard-for-in -- Validity is a system defined object that has no additional properties that we need to check for. */
            for (const validationProperty in element.validity) {
                if (element.validity[validationproperty]) {
                    const message = this.#createMessage(element, validationProperty)
                    if (message) {
                        errorMessage += `${message}\n`;
                    }
                }
            }

            return errorMessage.trim();
        }
    }

    /**
     * Hides the error marker for a validated element.
     * Removes visual error indicators and error messages.
     * @param {HTMLElement} element The element to hide the error for.
     * 
     * @memberof Validation
     */
    hideError(element) {
        element?.validator?.hideError();
    }

    /**
     * Validates if a given string can be evaluated as a number.
     * Supports integers, floats, negative numbers, and positive integer-only validation.
     * @param {string} numericText The string to validate as a number.
     * @param {boolean} [positiveIntOnly=false] True to check exclusively for positive integers, false to allow 0, negative and decimal numbers.
     * @returns {boolean} True when the text represents a valid number, false otherwise.
     * 
     * @memberof Validation
     */
    isNumeric(numericText, positiveIntOnly) {
        if (positiveIntOnly) {
            // Match only digits, no leading zeros.
            const positiveNumberRegex = /^([1-9][0-9]*)$/;

            return positiveNumberRegex.test(numericText);
        }

        const numberRegex = /^(-)?(\d*)(\.?)(\d*)$/;

        return numberRegex.test(numericText);
    }

    /**
     * Clear any error messages on the element.
     * @param {HTMLElement} element The (input/form) element to clear the message(s) for.
     * @param {Boolean} all True to reset every element child.
     */
    resetValidity(element, all = false) {
        if (all) {
            element = element.querySelector("form");
        }

        if (element.nodeName === "FORM") {
            [...element.elements].forEach((formelement) => {
                this.hideError(formelement);
            });

            // Any bubbles still left?
            [...element.querySelectorAll(".bubble")].forEach((bubble) => {
                bubble.parentElement.removeChild(bubble);
            });
        }
        else {
            this.hideError(element);
        }
    }

    /**
     * Scrolls an element into view without giving it focus.
     * Used to ensure error messages are visible to the user.
     * @param {HTMLElement} element The element that should be visible.
     * @param {number} [scrollOffset=0] Pixels to use as buffer for positioning (useful for fixed headers).
     * 
     * @memberof Validation
     */
    scrollIntoView(element, scrollOffset) {
        if (!element || core.isElementInView(element, scrollOffset)) {
            return;
        }

        const container = this.#getScrollableContainer(element) ?? element.ownerDocument.documentElement;
        const { top } = element.getBoundingClientRect();
        const scrollTop = container.scrollTop;

        container.scrollTo({
            behavior: 'smooth',
            top: top + scrollTop - scrollOffset
        });
    }

    /**
     * Sets the error message.
     * @param {HTMLELement} element The element to set the error message on.
     * @param {String} message The message to use.
     * @return {Boolean} Always false as we're setting an error message.
     */
    setErrorMessage(element, message) {
        element.dataset.validationmessage = message;

        return false;
    }

    /**
     * Displays an error marker, attached to the element.
     * @param {Object} element The element the error should be attached to.
     * @param {String} msg The message to display.
     */
    showError(element, msg) {
        // When an element is validated, but invisible, the error is not shown (nothing to attach to).
        // If the validatorparent is set and visible we can display an error.
        const validatorParent = element.dataset?.validatorparent;
        const validatorParentElement = element.closest("form").querySelector(validatorParent) ?? element.parentElement;

        if (core.isVisible(element) || core.isVisible(validatorParentElement)) {
            // If no message is provided, try to get it from the element.
            msg ??= this.getMessage(element);

            if (!element.validator) {
                // Other libraries might have messed up the reference.
                element.validator = new validator({
                    field: element,
                    hideTimeout: this.#hideTimeOut,
                    validatorParent: validatorParent
                });
            }

            element.validator.showError({ message: msg });
        }

        // We're showing an error, always return false.
        return false;
    }

    /**
     * Validate the element.
     * @param {Event} event The event data passed.
     * @param {HTMLElement} element The element (input) to check.
     * @returns {Boolean} True when the input is valid, false when invalid.
     */
    validateAdditional({ event = null, element = null }) {
        let isValid = true;

        const forceValidate = element?.hasAttribute("data-force-validate");

        if (isValid || forceValidate) {
            isValid = this.validateFormat({ event: event, element: element });
        }

        if ((isValid || forceValidate) && element?.hasAttribute("data-dateformat")) {
            isValid = this.validateDateTime({ event: event, element: element, format: element.dataset.dateformat });
        }

        if ((isValid || forceValidate) && element?.hasAttribute("data-accept") && element?.hasAttribute("data-files") && element.files.length) {
            isValid = this.validateAccept({ event: event, element: element });
        }

        if ((isValid || forceValidate) && element?.hasAttribute("data-filesize")) {
            isValid = this.validateFileSize({ event: event, element: element });
        }

        return isValid;
    }

    /**
     * Validate the accept parameter.
     * @param {HTMLElement} element The element (input) to check.
     * @returns {Boolean} True when the input is valid, false when invalid.
     */
    validateAccept({ element = null }) {
        const files = element?.files;
        const acceptedArray = this.#commaSplit(element?.accept);

        if (files?.length > 0 && acceptedArray?.length > 0) {
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const extension = file.name.substr(file.name.lastIndexOf("."));

                if (acceptedArray.includes(extension)) {
                    return true;
                }
            }
        }

        return this.setErrorMessage(element,
                                   (element.dataset.invalidaccept ?? this.validationMessages.invalidAccept).replace("{Extensions}", acceptedArray));
    }

    /**
     * Validates if two elements are equal.
     * @param {HTMLElement} element The element (input) to check.
     * @returns {Boolean} True when the input is valid, false when invalid.
     */
    validateCompare({ element = null }) {
        const compareTo = element.dataset.compareto;
        const compareToElement = element.form.querySelector("#" + compareTo);

        if (compareToElement) {
            if (element.value === compareToElement.value) {
                return true;
            }
        }

        return this.setErrorMessage(element, element.dataset.comparetomessage);
    }

    /**
     * Validate if the provided string can be parsed to a valid datetime for the format given.
     * @param {HTMLElement} element The element (input) to check.
     * @param {String} format The string that sets the format that it's suppose to be.
     * @returns {Boolean} True when the input is valid, false when invalid.
     */
    validateDateTime({ element = null, format = null }) {
        try {
            // Check first if we have a valid date in the first place.
            const isDateString = dating.isDateStr(element.value);

            if (isDateString === "") {
                // Get a standard ISO 8601 string.
                const parsedDate = dating.parseDateString(element.value, format);

                if (!isNaN(Date.parse(parsedDate))) {
                    return true;
                }
            }

            return this.setErrorMessage(element, element.dataset.invaliddatemessage ?? this.validationMessages.invalidDate);
        }
        catch {
            return this.setErrorMessage(element, element.dataset.invaliddatemessage ?? this.validationMessages.invalidDate);
        }
    }

    /**
     * Validate the allowed file size.
     * @param {HTMLElement} element The element (input) to check.
     * @returns {Boolean} True when the input is valid, false when invalid.
     */
    validateFileSize({ element = null }) {
        const files = element?.files;

        if (files?.length > 0) {
            const allowedSize = parseInt(element.getAttribute("filesize") ?? "0.01");

            if (files[0].size < allowedSize) {
                return true;
            }
        }

        return false;
    }

    /**
     * Validates all elements in a form, including web components.
     * This is the main validation method that should be called before form submission.
     * @param {Object} options The validation options.
     * @param {HTMLDocument} [options.container=document] The document to scan for forms and validation elements.
     * @param {HTMLFormElement} [options.form] The specific form to validate. If not provided, the first form in container is used.
     * @param {number} [options.scrollOffset=0] Pixels to use as buffer for scroll positioning (useful for fixed headers).
     * @param {boolean} [options.validateAll=true] True to validate all elements, false to stop on first invalid result.
     * @param {Function} [options.customValidation] Custom validation function to run. Should return true if valid, false if invalid.
     * @returns {boolean} True when all validation passes, false when any validation fails.
     * 
     * @memberof Validation
     */
    validateForm({ container, form, scrollOffset = 0, validateAll = true, customValidation = null }) {
        // When no form is provided, we assume we're validating the given container or the whole document when container is null.
        container ??= document;
        form ??= container.querySelector("form");

        // Local function to validate individual elements.
        // Web components are validated through their attached validators which call their validate() method.
        const validateElement = (element) => {
            // When the parent element is hidden always return true.
            // When we have the 'data-force-validate' attribute something else is at play.
            const forceValidate = element?.hasAttribute("data-force-validate");

            if ((!forceValidate && !core.isVisible(element)) || !core.isVisible(element.parentElement)) {
                return true;
            }

            // Validate the element using the standard validator.
            if (element.validator) {
                element.validator.isFormValidation = true;

                // When doing form validation we also validate the required elements.
                return element.validator.validate({ required: true });
            }

            // When validation isn't possible (no validator found), return true.
            return true;
        }

        // We assume valid.
        let isValid = true;

        // Check if we should validate in the first place.
        const ignoreValidation = !!form?.hasAttribute("formnovalidate");

        if (!ignoreValidation) {
            const elements = [...form.elements];

            // Add elements with data-validator attribute.
            elements.push(...[...document.querySelectorAll("[data-validator]")]);

            // Add all web components in the form that have a validate method.
            const webComponents = [...form.querySelectorAll('*')].filter(el =>
                el.tagName && el.tagName.includes('-') && typeof el.validate === 'function'
            );

            elements.push(...webComponents);

            // 'Every' returns false immediately when a 'falsy' value is returned from the inner callback.
            if (!validateAll) {
                isValid = elements.every((element) => {
                    return validateElement(element);
                });
            }
            else {
                elements.forEach((element) => {
                    if (!validateElement(element)) {
                        isValid = false;
                    }
                });
            }

            // Do we need to run some more? The customValidation function should return true when valid and false when not.
            if (customValidation) {
                const validationResult = customValidation();

                // Only modify isValid variable when custom validation fails, do not revert previous invalid results.
                if (!validationResult) {
                    isValid = false;
                }
            }
        }

        // When isValid is false, we have error bubbles.
        if (!isValid) {
            this.scrollIntoView(container.querySelector(".bubble"), scrollOffset);
        }

        return isValid;
    }

    /**
     * Check the element format (custom) property.
     * @param {Event} event The event data passed.
     * @param {HTMLElement} element The element to get the type for.
     * @returns {Boolean} True when the input is valid, false when invalid.
     */
    validateFormat({ event = null, element = null }) {
        // But when we have the 'data-forcevalidate' attribute something else is at play.
        const forceValidate = element?.hasAttribute("data-force-validate");

        if (!forceValidate && (!core.isVisible(element) || element.hasAttribute("disabled"))) {
            return true;
        }

        const format = element.getAttribute("format");

        switch (format) {
            case "date":
                return this.#validateDate({ event, element });

            case "email":
                return this.#validateEmail({ event, element });

            case "number":
                return this.validateNumber({ event, element });
            
            case "text":
                return this.#validateMinMax({ event, element });

            default:
                return true;
        }
    }

    /**
     * Validates checkbox/radio groups for minimum selection requirements.
     * Checks if the required number of items are selected in the group.
     * @param {Object} options The validation options.
     * @param {HTMLElement} options.element The element to validate (typically first in group).
     * @param {HTMLElement[]} options.items All elements in the group.
     * @returns {boolean} True when the group meets minimum selection requirements, false otherwise.
     * 
     * @memberof Validation
     */
    validateGroup({ element = null, items = null }) {
        let itemsChecked = +element?.dataset?.minimumchecked;

        if (isNaN(itemsChecked)) {
            itemsChecked = 1;
        }

        if (items.filter((item) => item.checked).length < itemsChecked) {
            if (itemsChecked === 1) {
                // When the element is a radio group or a single checkbox, use the default required message.
                this.setErrorMessage(element,
                                     element.dataset.valuemissingmessage ??
                                        (element.type === "radio" || items.length === 1
                                        ? this.validationMessages.required
                                        : this.validationMessages.selectOne));
            }
            else {
                this.setErrorMessage(element,
                        (element.dataset.valuemissingmessage ?? this.validationMessages.selectMinimum)
                        .replace("{itemschecked}", itemsChecked));
            }

            // Indicate all members of the group as invalid.
            [...items].forEach((item) => {
                item.validator?.setInvalid();
            });

            return false;
        }

        return true;
    }

    /**
     * Validates numeric input according to configured rules.
     * Supports min/max values, decimal places, positive integer only, and range validation.
     * @param {Object} options The validation options.
     * @param {HTMLInputElement} options.element The input element to validate.
     * @returns {boolean} True when the input is valid, false when invalid.
     * 
     * @memberof Validation
     */
    validateNumber({ element = null }) {
        // Empty is validated by the 'required' property.
        if (element == null || element.value.trim() === "") {
            return true;
        }

        if (!this.isNumeric(element.value, element.hasAttribute("data-whole-positive-only"))) {
            // Not valid.
            this.setErrorMessage(element, element.dataset.invalidnumbermessage ?? this.validationMessages.invalidNumber);

            return false;
        }

        let isValid = true;
        const forceValidate = element.hasAttribute("data-force-validate");

        const decimalsRequired = parseInt(element.getAttribute("decimals"));
        const decimalValue = element.value?.split(".") ?? "";

        if (decimalValue.length > 1 && decimalValue[1].length > decimalsRequired) {
            // Not valid.
            this.setErrorMessage(element,
                (element.dataset.invaliddecimalsmessage ?? this.validationMessages.invalidDecimals).replace("{decimals}", decimalsRequired));

            isValid = false;

            if (!forceValidate) {
                return isValid;
            }
        }

        // Boolean checks on these variables work correctly with 0 because type is string.
        let min = element.getAttribute("Min"),
            max = element.getAttribute("Max"),
            minRange = element.getAttribute("MinRange"),
            maxRange = element.getAttribute("MaxRange");

        // None of the properties are set.
        if (!min && !max && !minRange && !maxRange) {
            return true;
        }

        // Set range state.
        this.validateRangeState({ element });

        // Validate value (min/max).
        let val = parseFloat(element.value),
            minOk = true,
            maxOk = true;

        if (min) {
            minOk = val >= parseFloat(min);
        }

        if (max) {
            maxOk = val <= parseFloat(max);
        }

        if (!minOk || !maxOk) {
            if (min && max) {
                // Not valid.
                this.setErrorMessage(element,
                    (element.dataset.invalidrangemessage ?? this.validationMessages.invalidRange)
                        .replace("{rangestart}", min).replace("{rangeend}", max));

                isValid = false;
                if (!forceValidate) {
                    return isValid;
                }
            }
            else if (!minOk) {
                // Not valid.
                this.setErrorMessage(element,
                    (element.dataset.invalidrangeminmessage ?? this.validationMessages.invalidRangeMin)
                        .replace("{rangestart}", min));

                isValid = false;
                if (!forceValidate) {
                    return isValid;
                }
            }
            else if (!maxOk) {
                // Not valid.
                this.setErrorMessage(element,
                    (element.dataset.invalidrangemaxmessage ?? this.validationMessages.invalidRangeMax).replace("{rangeend}", max));

                isValid = false;
                if (!forceValidate) {
                    return isValid;
                }
            }
        }

        return isValid;
    }

    /** 
     * Check the input whether a value is within a given 'check range'; setting the background color to indicate validation state (without error).
     * @param {HTMLInputElement} element The input to validate.
     */
    validateRangeState({ element = null }) {
        if (element == null) {
            return;
        }

        // When a value an empty string or the value is not numeric.
        if (element.value.trim() === "" || !this.isNumeric(element.value, element.hasAttribute("data-whole-positive-only"))) {
            // Remove any color indicators.
            element.classList.remove("range-not-ok");
            element.classList.remove("range-ok");

            return;
        }

        // Ranges are an indicator about expected values, indicating the user about the validity of the content without forcing it.
        let val = parseFloat(element.value),
            minRange = element.getAttribute("MinRange"),
            maxRange = element.getAttribute("MaxRange"),
            minRangeOk = true,
            maxRangeOk = true;

        // When neither value is set, return.
        if (!minRange && !maxRange) {
            return;
        }

        if (minRange) {
            minRangeOk = val >= parseFloat(minRange);
        }

        if (maxRange) {
            maxRangeOk = val <= parseFloat(maxRange);
        }

        let rangeNotOk = !minRangeOk || !maxRangeOk,
            rangeOk = !rangeNotOk && Boolean(minRange || maxRange);

        element.classList.toggle("range-not-ok", rangeNotOk);
        element.classList.toggle("range-ok", rangeOk);
    }

    /**
     * Validates if a given readonly input adheres to any rules (required only for now).
     * @param {Event} event The event data passed.
     * @param {HTMLInputElement} element The input to validate.
     * @returns {Boolean} True when the input is valid, false when invalid.
     */
    validateReadOnly({ event = null, element = null }) {
        if (element == null || !element.hasAttribute("readonly")) {
            // Attibute is missing, thus return true.
            return true;
        }

        return this.validateRequired({ event: event, element: element });
    }

    /**
     * Validates required field constraints.
     * Checks if required fields have values and are not empty.
     * @param {Object} options The validation options.
     * @param {HTMLInputElement} options.element The input element to validate.
     * @returns {boolean} True when required validation passes, false when required field is empty.
     * 
     * @memberof Validation
     */
    validateRequired({ element = null }) {
        if (element && element.hasAttribute("required") && element.value?.trim() === "") {
            return this.setErrorMessage(element, element.dataset.valuemissingmessage ?? this.validationMessages.valuemissing);
        }

        return true;
    }

    /**
     * Compares two string values for a given comparer.
     * @param {String} leftValue The first value to check.
     * @param {String} operator The operator to use.
     * @param {String} rightValue The (second) value to compare against.
     * @returns {Boolean} How the comparison evaluated.
     */
    validateValueCompare({ leftValue = null, operator = null, rightValue = null }) {
        let isValid = false,
            parsedLeftValue,
            parsedRightValue;

        if (operator !== "contains") {
            if (this.#isDateValue(leftValue) && this.#isDateValue(rightValue)) {
                parsedLeftValue = dating.getDateFromString(leftValue);
                parsedRightValue = dating.getDateFromString(rightValue);
            }
            else if (!isNaN(leftValue) && !isNaN(rightValue)) {
                parsedLeftValue = parseFloat(leftValue);
                parsedRightValue = parseFloat(rightValue);
            }
            else {
                parsedLeftValue = leftValue;
                parsedRightValue = rightValue;
            }
        }
        else {
            parsedLeftValue = leftValue?.toLowerCase() ?? "";
            parsedRightValue = rightValue?.toLowerCase().trim() ?? "";
        }
        
        if (operator === "=" && parsedLeftValue == parsedRightValue) { isValid = true; }
        if (operator === "<>" && parsedLeftValue != parsedRightValue) { isValid = true; }
        if (operator === ">" && parsedLeftValue > parsedRightValue) { isValid = true; }
        if (operator === "<" && parsedLeftValue < parsedRightValue) { isValid = true; }
        if (operator === ">=" && parsedLeftValue >= parsedRightValue) { isValid = true; }
        if (operator === "<=" && parsedLeftValue <= parsedRightValue) { isValid = true; }
        if (operator === "contains" && parsedLeftValue.length > 0 && parsedLeftValue.indexOf(parsedRightValue) > -1) { isValid = true; }

        return isValid;
    }
    // #endregion ********************************************************************************/

    // #region: Private Methods ******************************************************************/
    /**
     * Split & trim the item in a comma delimited string.
     * @param {String} string The string to split on any comma present.
     */
    #commaSplit(string) {
        return string.split(",").map((item) => {
            return item.trim();
        })
        .filter((item) => {
            return item;
        });
    }

    /**
     * Creates a validation message for the given element and the validation property to get the message for.
     * Prefers any custom message from a dataset property.
     * @param {HTMLElement} element The element to create the message for.
     * @param {String} validationProperty The property to create the message for.
     * @returns {String} The message creted.
     */
    #createMessage(element, validationProperty) {
        return element.dataset[`${validationProperty?.toLowerCase()}message`]
            ?? element.dataset.validationmessage
            ?? this.validationMessages[`${element.type}${validationProperty?.toLowerCase()}`]
            ?? this.validationMessages[validationProperty?.toLowerCase()]
            ?? (element.validationMessage !== "" ? element.validationMessage : null)
            ?? this.validationMessages["default"]
            ?? "An error has occurred.";
    }

    /**
     * What type of element are we validating.
     * @param {HTMLElement} element The element to get the type for.
     */
    #getElementType(element) {
        if (element.nodeName === "TEXTAREA") {
            return "textarea";
        }
        else if (element.nodeName === "SELECT") {
            return element.hasAttribute("multiple") ? "select-multiple" : "select-one";
        }
        else if (element.nodeName === "INPUT") {
            return element.getAttribute("type") || element.type || "text";
        }

        return "";
    }

    /**
     * Finds the scrollable container when in a dialog.
     * @param {HTMLElement} element
     * @returns
     */
    #getScrollableContainer(element) {
        let parent = element?.parentElement;

        while (parent) {
            const overflowY = getComputedStyle(parent).overflowY;

            if ((overflowY === 'auto' || overflowY === 'scroll') && parent.scrollHeight > parent.clientHeight) {
                return parent;
            }

            parent = parent.parentElement;
        }

        return null;
    }

    /**
     * Checks if a string can represent a date, time or a combination of the two.
     * @param {String} dateString The string to validate.
     * @returns {Boolean} True when the string represents a valid date, false if not.
     */
    #isDateValue(dateString) {
        if (!dateString || !dateString.trim()) {
            return false;
        }

        var message = dating.isDateStr(dateString);
        if (message !== "") {
            return false;
        }

        return true;
    }

    /**
     * Validates if a given input adheres to its rules (being a date/time).
     * @param {HTMLInputElement} element The input to validate.
     * @returns {Boolean} True when the input is valid, false when invalid.
     */
    #validateDate({ element = null }) {
        let dateString = element?.value;

        // Empty means true. Empty is validated by the 'required' property.
        if (!dateString?.trim()) {
            return true;
        }

        let typeIdMap = { "0": "date", "1": "time", "2": "both" },
            typeId = element.getAttribute("datetype"),
            message = dating.isDateStr(dateString, typeIdMap[typeId]);

        if (message) {
            // Not valid.
            return this.setErrorMessage(element, element.dataset.invaliddatemessage ?? message);
        }

        return true;
    }

    /**
     * Validates if a given input adheres to its rules (being an email address).
     * @param {HTMLInputElement} element The input to validate.
     * @returns {Boolean} True when the input is valid, false when invalid.
     */
    #validateEmail({ element = null }) {
        // Can't start or end with a period, can't contain whitespace, can only contain a single @.
        const filter = /^[^\.@\s][^@\s]*@[^@\s]+\.[^@\s\.]+$/;

        // Empty is validated by the 'required' property.
        if (!element?.value || filter.test(element.value)) {
            return true;
        }

        // Not valid.
        return this.setErrorMessage(element, element.dataset.invalidemailmessage ?? this.validationMessages.invalidEmail);
    }

    /**
     * Validates if a given input adheres to its rules (min chars, max chars).
     * Empty is valid, emptiness should be validated by 'required'.
     * @param {Event} event The event data passed.
     * @param {HTMLInputElement} element The input to validate.
     * @returns {Boolean} True when the input is valid, false when invalid.
     */
    #validateMinMax({ event = null, element = null }) {
        if (!element?.value.trim()) {
            return true;
        }

        let isValid = true;

        const forceValidate = element?.hasAttribute("data-force-validate");
        const minChars = element.getAttribute("MinChars");
        const maxChars = element.getAttribute("MaxChars");
        const characterLength = element.value.length;
        const isInputEvent = event?.type === "input";

        if ((!isInputEvent || element.validator?.hasBubble()) && minChars && characterLength < minChars) {
            // Not valid.
            this.setErrorMessage(element,
                (element.dataset.invalidmincharsmessage ?? this.validationMessages.invalidMinChars)
                    .replace("{typed}", characterLength)
                    .replace("{characters}", minChars));
            
            isValid = false;

            if (!forceValidate) {
                return isValid;
            }
        }

        if (maxChars && characterLength > maxChars) {
            // Not valid.
            this.setErrorMessage(element,
                (element.dataset.invalidmaxcharsmessage ?? this.validationMessages.invalidMaxChars)
                    .replace("{typed}", characterLength)
                    .replace("{characters}", maxChars));

            isValid = false;
        }

        return isValid;
    }
    // #endregion ********************************************************************************/

}();
// #endregion ************************************************************************************/